<template>
  <div :class="classes">
    <div class="imageWrap">
      <Image v-bind="imageConfiguration" />
    </div>

    <div class="textWrap">
      <h1>
        <Heading
          v-if="heading_1"
          :content="'<span>' + heading_1 + '</span>'"
          tag="span"
          role="extra"
          size="large"
        />

        <template v-if="heading_2">
          <br />
          <Heading
            :content="'<span>' + heading_2 + '</span>'"
            tag="span"
            role="extra"
            size="large"
          />
        </template>
      </h1>

      <template v-if="heading_3">
        <div class="textDescription" v-html="formattedHeading3"></div>
      </template>

      <div class="buttons" v-if="buttonElements.length">
        <template v-for="(button, index) in buttonElements">
          <LinkButton
            v-if="button.buttonUrl && button.buttonContent"
            :key="index"
            :button-type="button.buttonConfiguration"
            state="default"
            theme="light"
            :url="button.buttonUrl"
          >
            {{ button.buttonContent }}
          </LinkButton>
        </template>
      </div>

      <div class="awards" v-if="awardsElement && awardImages.length">
        <figure
          class="image_container"
          v-for="(award, index) in awardImages"
          :key="index"
        >
          <img :src="award.src" :alt="award.alt || ''" />
        </figure>
      </div>
      <div v-if="mobileStoreButtons" class="mobileStoreButtons">
        <MobileAppTeaser :heading="mobileStoreHeading" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import Heading from 'ewi-ui/components/1-atoms/Heading';
import LinkButton from 'ewi-ui/components/1-atoms/LinkButton/LinkButton.vue';
import MobileAppTeaser from 'ewi-ui/components/3-organisms/FooterNavigation/MobileAppTeaser.vue';
import Image, { type ImageProps } from 'ewi-ui/components/1-atoms/Media/Image';
import { ButtonType } from 'ewi-ui-helpers/types';

export interface StageHeroProps {
  heading_1?: string;
  heading_2?: string;
  heading_3?: string;
  textContainerWidth?: string;
  textContainerPosition?: string;
  textHorizontalAlignment?: string;
  textVerticalAlignment?: string;
  mobileTextContainerWidth?: string;
  mobileTextContainerPosition?: string;
  mobileTextHorizontalAlignment?: string;
  mobileTextVerticalAlignment?: string;
  textColor?: string;
  reducedHeight?: boolean;
  buttonElements?: Array<{
    buttonConfiguration: ButtonType;
    buttonContent: string;
    buttonUrl: string;
  }>;
  awardsElement?: boolean;
  awardImages?: Array<{ src: string; alt?: string }>;
  hasOverlay?: boolean;
  clipLeftMax?: boolean;
  clipLeftXL?: boolean;
  clipLeft?: boolean;
  pushLeft?: boolean;
  imageConfiguration?: ImageProps;
  mobileStoreButtons?: boolean;
  mobileStoreHeading?: string;
}

const props = withDefaults(defineProps<StageHeroProps>(), {
  heading_1: '',
  heading_2: '',
  heading_3: '',
  textContainerWidth: '',
  textContainerPosition: '',
  textHorizontalAlignment: 'left',
  textVerticalAlignment: 'center',
  mobileTextContainerWidth: '',
  mobileTextContainerPosition: '',
  mobileTextHorizontalAlignment: '',
  mobileTextVerticalAlignment: '',
  textColor: 'white',
  reducedHeight: false,
  buttonElements: () =>
    [] as Array<{
      buttonConfiguration: ButtonType;
      buttonContent: string;
      buttonUrl: string;
    }>,
  awardsElement: true,
  awardImages: () => [],
  hasOverlay: false,
  clipLeftMax: false,
  clipLeft: false,
  pushLeft: false,
  mobileStoreButtons: false,
  mobileStoreHeading: '',
  imageConfiguration: () =>
    ({
      defaultSrc: '',
      imageCaptionPosition: 'left',
      imageRoundedCorners: true,
    }) as ImageProps,
});

const formattedHeading3 = computed(() => {
  if (!props.heading_3) return '';

  const trimmedContent = props.heading_3.trim();
  if (
    trimmedContent.startsWith('<p>') ||
    trimmedContent.startsWith('<ul>') ||
    trimmedContent.startsWith('<ol>')
  ) {
    return trimmedContent;
  }

  return `<p>${trimmedContent}</p>`;
});

const classes = computed(() => ({
  'm-stage-hero': true,
  'has-overlay': props.hasOverlay,
  'clip-left-max': props.clipLeftMax,
  'clip-left-xl': props.clipLeftXL,
  'clip-left': props.clipLeft,
  'push-left': props.pushLeft,
  [`text-horizontal-${props.textHorizontalAlignment}`]:
    props.textHorizontalAlignment !== '',
  [`text-vertical-${props.textVerticalAlignment}`]:
    props.textVerticalAlignment !== '',
  [`text-container-width-${props.textContainerWidth}`]:
    props.textContainerWidth !== '',
  [`text-container-position-${props.textContainerPosition}`]:
    props.textContainerPosition !== '',
  [`mobile-text-horizontal-${props.mobileTextHorizontalAlignment}`]:
    props.mobileTextHorizontalAlignment !== '',
  [`mobile-text-vertical-${props.mobileTextVerticalAlignment}`]:
    props.mobileTextVerticalAlignment !== '',
  [`mobile-text-container-width-${props.mobileTextContainerWidth}`]:
    props.mobileTextContainerWidth !== '',
  [`mobile-text-container-position-${props.mobileTextContainerPosition}`]:
    props.mobileTextContainerPosition !== '',
  [`color-${props.textColor}`]: props.textColor !== '',
  'reduced-height': props.reducedHeight,
}));
</script>
<style src="./StageHero.scss"></style>
